import { lazy } from 'react';

export type MenuItemType = {
    id?: number | null;
    label?: string | null;
    to_url?: string | null;
    parent_id?: number | null;
    component?: string | null;
    icon?: string | null;
    has_menu?: boolean | null;
    cashier_required?: boolean | null;
    c_at?: string | null;
    c_by?: number | null;
    m_at?: string | null;
    m_by?: number | null;
    [x: string]: unknown;
};

/**
 * Dynamically load a component by name
 * Tries to load from pages folder with proper error handling
 */
export const loadComponentByName = (componentName?: string): React.ComponentType<any> | null => {
    if (!componentName) {
        return null;
    }

    try {
        // Dynamically import component from pages folder
        // This creates a chunk that loads the component on demand
        const Component = lazy(() =>
            import(`../pages/${componentName}`).catch(error => {
                // eslint-disable-next-line no-console
                console.error(`Failed to load component: ${componentName}`, error);
                // Return a fallback component
                return import(`../pages/NotFoundPage`);
            })
        );
        return Component;
    } catch (error) {
        // eslint-disable-next-line no-console
        console.warn(`Component "${componentName}" could not be loaded. Falling back to NotFoundPage.`);
        return null;
    }
};

/**
 * Generate routes from menu items
 * Filter menu items that have a component and to_url defined (regardless of has_menu status)
 * Routes should be accessible even if not shown in the menu
 */
export const getRoutesFromMenus = (menus: MenuItemType[]): MenuItemType[] => {
    return menus.filter(
        menu => menu.component && menu.to_url
    );
};

/**
 * Get all menu items organized in a tree structure by parent_id
 */
export const getMenuTreeFromMenus = (menus: MenuItemType[]): MenuItemType[] => {
    // Return only root menus (parent_id is null or undefined)
    return menus.filter(menu => !menu.parent_id).sort((a, b) => ((a.id as number) || 0) - ((b.id as number) || 0));
};

/**
 * Get children for a parent menu
 */
export const getChildMenus = (parentId: number, menus: MenuItemType[]): MenuItemType[] => {
    return menus
        .filter(menu => menu.parent_id === parentId)
        .sort((a, b) => ((a.id as number) || 0) - ((b.id as number) || 0));
};
